import React, { Component, type ReactNode } from 'react';
import { motion } from 'motion/react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { captureException } from '../lib/sentry';

/* ── Branded fallback UI (functional component) ── */
function ErrorFallback({ onRetry }: { onRetry: () => void }) {
  // Inline i18n strings to avoid pulling the entire 164KB i18n bundle into the eager load
  const isZh = typeof navigator !== 'undefined' && navigator.language.startsWith('zh');
  const tr = isZh
    ? { title: '页面出了点问题', desc: '发生了意外错误，页面崩溃了。', retry: '重新加载', goHome: '返回首页' }
    : { title: 'Something went wrong', desc: 'An unexpected error occurred. The page crashed.', retry: 'Reload page', goHome: 'Go home' };

  return (
    <div className="min-h-screen bg-surface-base flex flex-col">
      {/* Brand header */}
      <header className="px-6 py-4 flex items-center">
        <a href="/" className="flex items-center gap-2.5">
          <img src="/logo.webp" alt="POPWIZ" className="w-9 h-9 object-contain" />
          <span
            className="text-xl font-bold tracking-[0.02em] text-black uppercase"
            style={{ fontFamily: '"Montserrat", sans-serif' }}
          >
            POPWIZ
          </span>
        </a>
      </header>

      {/* Content */}
      <main className="flex-1 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
          className="text-center max-w-sm"
        >
          {/* Icon */}
          <div className="mx-auto mb-5 w-16 h-16 rounded-2xl bg-brand-orange/[0.08] flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-brand-orange/60" />
          </div>

          {/* Status badge */}
          <p className="text-xs font-mono text-txt-tertiary tracking-widest mb-3">
            ERROR 500
          </p>

          {/* Headline */}
          <h1
            className="text-2xl sm:text-3xl font-bold text-txt mb-3"
            style={{ fontFamily: '"Montserrat", sans-serif' }}
          >
            {tr.title}
          </h1>

          {/* Description */}
          <p className="text-sm text-txt-secondary mb-8 leading-relaxed">
            {tr.desc}
          </p>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={onRetry}
              className="inline-flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-brand-orange rounded-xl hover:opacity-90 transition-opacity"
            >
              <RotateCcw className="w-4 h-4" />
              {tr.retry}
            </button>
            <a
              href="/"
              className="inline-flex items-center justify-center px-6 py-2.5 text-sm text-txt-secondary hover:text-txt rounded-xl border border-border hover:border-txt-tertiary transition-colors"
            >
              {tr.goHome}
            </a>
          </div>
        </motion.div>
      </main>
    </div>
  );
}

/* ── Error boundary (class component, wraps BrowserRouter) ── */
interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
    captureException(error, { extra: { componentStack: info.componentStack } });
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback onRetry={() => window.location.reload()} />;
    }
    return this.props.children;
  }
}
